import React, { useState, useEffect } from 'react';
import Loader from './Loader';

const SimpleEvolutionGraph = ({ data, min, max, ...props }) => {
	const container = props?.container ?? false;

	const [ is_loading, setIsLoading ] = useState((container !== false) ? true : false);
	const [ original_w, setOriginalWidth ] = useState(props?.width ?? 280);

	const updateChartWidth = () => {
		const item = document.querySelector(container);
		if(item.offsetWidth != undefined) setOriginalWidth(item.offsetWidth);
	};

	useEffect(() => {
		if(container === false) return;

		window.addEventListener('resize', updateChartWidth);

		setIsLoading(false);

		// need to put this in a timeout to avoid being full-window-width on first load
		setTimeout(() => {
			updateChartWidth();
		}, 500);

		return () => {
			window.removeEventListener('resize', updateChartWidth);
		}
	}, []);

	const original_h = props?.height ?? 80;

	if(is_loading) {
		return (
			<div style={{ width:'100%', height:`${original_h}px`, display:'flex', justifyContent:'center', alignItems:'center' }}>
				<Loader />
			</div>
		)
	}

	const color = props?.color ?? '120, 195, 0';

	const use_gradient = props?.use_gradient ?? false;
	const use_perspective_points = props?.use_perspective_points ?? true;
	const line_width = props?.line_width ?? 3;
	const point_radius = props?.point_radius ?? 4;
	const perspective_growth = Math.min((props?.perspective_growth ?? 1.5), (line_width - 0.1));

	const display_points = ((point_radius * 2) > line_width) ? true : false;

	let padding = props?.padding ?? point_radius;
	if(display_points) padding = Math.max(padding, point_radius);

	const expected = props?.expected ?? false;

	const width = original_w - 2 * padding;
	const height = original_h - 2 * padding;
	const point_w = width / (data.length - 1);
	const bottom_area_height = 0;	// area from relative 0 to the bottom of the graph
	const curve_height_spread = height - bottom_area_height;
	const data_height_range = max - min;

	const getPointCoords = (point, p) => {
		const relative_point = point - min;
		const x = p * point_w;
		const y = curve_height_spread - curve_height_spread * relative_point / data_height_range;
		return { x, y };
	};

	let initial_y = 0;
	const relative_data = [];
	const expected_data = [];
	data.map((point, p) => {
		const coord1 = getPointCoords(point, p);
		const x1 = coord1.x;
		const y1 = coord1.y;

		if(p >= data.length - 2 && expected !== false) {
			const coord2 = getPointCoords(expected, p);
			const y2 = coord2.y;
	
			expected_data.push(`${x1}, ${(p == data.length - 2) ? y1 : y2}`);
		}

		if(p == 0) initial_y = y1;

		relative_data.push(`${x1}, ${y1}`);
	});

	const step = perspective_growth / (relative_data.length - 1);

	const relative_data_last_month = [...relative_data].splice(0, relative_data.length - 1);

	return (
		<svg
			version="1.1"
			viewBox={`${-padding} ${-padding} ${width + 2 * padding} ${height + 2 * padding}`}
			preserveAspectRatio='xMinYMin meet'
			width={original_w}
			height={original_h}
			//style={{ enableBackground:`new 0 0 ${width} ${height}` }}
		>
			<defs>
				<linearGradient
					id={`grad_${props.chart_id}`}
					x1="0%"
					x2="0%"
					y1="0%"
					y2="100%"
				>
					<stop
						offset="0%"
						stop-color={`rgba(${color}, 0.5)`}
					/>

					<stop
						offset="100%"
						stop-color={`rgba(${color}, 0.2)`}
					/>
				</linearGradient>
			</defs>

			<polyline
				points={`${relative_data.join(" ")} ${width},${height} 0,${height} 0,${initial_y}`}
				fill={(use_gradient) ? `url(#grad_${props.chart_id})` : `rgba(${color}, 0.15)`}
			/>

			<polyline
				points={`${relative_data_last_month.join(" ")} ${width - point_w},${height} 0,${height} 0,${initial_y}`}
				fill={(use_gradient) ? `url(#grad_${props.chart_id})` : `rgba(${color}, 0.2)`}
			/>

			<line
				x1={0}
				x2={width}
				y1={height}
				y2={height}
				strokeWidth={1}
				stroke={`rgba(${color}, 0.3)`}
				style={{ vectorEffect:'non-scaling-stroke' }}
			/>

			{(bottom_area_height > 0) && (
				<line
					x1={0}
					x2={width}
					y1={height - bottom_area_height}
					y2={height - bottom_area_height}
					strokeWidth={1}
					stroke='rgba(0, 0, 0, 0.5)'
					strokeDasharray='4'
					style={{ vectorEffect:'non-scaling-stroke' }}
				/>
			)}

			{(expected !== false) && (
				<polyline
					points={expected_data.join(" ")}
					strokeWidth={1}
					fill='transparent'
					stroke='rgba(0, 0, 0, 1)'
					strokeDasharray='1 2'
					style={{ vectorEffect:'non-scaling-stroke' }}
				/>
			)}

			{(!use_perspective_points) && (
				<polyline
					points={relative_data_last_month.join(" ")}
					strokeWidth={line_width}
					stroke={`rgba(${color}, 1)`}
					fill='transparent'
					strokeLinecap='round'
					strokeLinejoin='round'
					style={{ vectorEffect:'non-scaling-stroke' }}
				/>
			)}

			{relative_data.map((point, p) => {
				if(p == 0) return null;
				if(!use_perspective_points && p < relative_data.length - 1) return null;

				const previous_coords = relative_data[p - 1].split(",");
				const coords = point.split(",");

				return (
					<line
						key={`line_${props.chart_id}_${p}`}
						x1={parseFloat(previous_coords[0])}
						y1={parseFloat(previous_coords[1])}
						x2={parseFloat(coords[0])}
						y2={parseFloat(coords[1])}
						strokeWidth={(p == relative_data.length - 1) ? 1.5 : ((line_width - perspective_growth) + p * step)}
						stroke={`rgba(${color}, 1)`}
						fill='transparent'
						strokeLinecap={(p == relative_data.length - 1) ? null : 'round'}
						strokeLinejoin={(p == relative_data.length - 1) ? null : 'round'}
						strokeDasharray={(p == relative_data.length - 1) ? '3' : null}
						style={{ vectorEffect:'non-scaling-stroke' }}
					/>
				)
			})}

			{display_points && relative_data.map((point, p) => {
				if(p == relative_data.length - 1) return null;

				const coords = point.split(",");

				return (
					<circle
						key={`point_${props.chart_id}_${p}`}
						cx={parseFloat(coords[0])}
						cy={parseFloat(coords[1])}
						r={(use_perspective_points) ? ((point_radius - perspective_growth) + p * step) : point_radius}
						fill={`rgba(${color}, 1)`}
						strokeWidth={1}
						stroke='rgba(255, 255, 255, 1)'
						style={{ vectorEffect:'non-scaling-stroke' }}
					/>
				)
			})}
		</svg>
	)
};

export default SimpleEvolutionGraph;